import { useEffect, useMemo, useState } from "react"
import { useRouter } from "next/router"
import useSWR, { mutate } from "swr"

import { deleteNovel as deleteNovelFn } from "src/models/tieup/novel/deleteNovel"
import { Novel } from "src/models/tieup/novel/Novel"
import { useLoading } from "../useLoading"
import { useTieupToast } from "../useTieupToast"
import routes from "../../../../routes"
import { useCommonConfirmDialog } from "../useCommonConfirmDialog"
import { fetchNovel } from "../../../models/tieup/novel/fetchNovel"
import { fetchNovelForUser } from "../../../models/tieup/novel/fetchNovelForUser"
import { fetchTieupedNovelForUser } from "../../../models/tieup/novel/fetchTieupedNovelForUser"

export type NovelApiKey = "myPageNovel" | "guestNovel" | "myPageTieUp"
/**
 * APIのノベルデータを参照する
 */
const useNovel = (
  novelId?: number | null,
  novelApiKey: NovelApiKey = "myPageNovel",
  tieupId: number | null = null,
) => {
  const router = useRouter()
  const [novel, setNovel] = useState<Novel>()
  const { fullScreenLoading } = useLoading()
  const { addToastInfo } = useTieupToast()

  const { confirmDialog } = useCommonConfirmDialog()

  // ログイン状態およびたいあっぷ状態によって、エンドポイントを出し分ける
  const { fetcherKey, fetcher } = useMemo(() => {
    if (!novelId) {
      return {
        fetcherKey: null,
      }
    }

    if (novelApiKey === "guestNovel") {
      return {
        fetcherKey: `/v1/novel/${novelId}`,
        fetcher: () => {
          return fetchNovel(novelId)
        },
      }
    }

    if (novelApiKey === "myPageNovel") {
      return {
        fetcherKey: `/v1/mypage/novel/${novelId}`,
        fetcher: () => {
          return fetchNovelForUser(novelId)
        },
      }
    }

    if (!tieupId) {
      return {
        fetcherKey: null,
      }
    }

    return {
      fetcherKey: `/v1/mypage/tieup/${tieupId}/novel/${novelId}`,
      fetcher: () => {
        return fetchTieupedNovelForUser(tieupId, novelId)
      },
    }
  }, [novelApiKey, novelId, tieupId])

  const { data, error } = useSWR(
    fetcherKey,
    async () => {
      if (!fetcher) {
        return undefined
      }

      return fetcher().catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error)
        // ひとまず404に遷移させておく
        router.push(routes.notFound())
      })
    },
    {
      // 自動更新OFF
      revalidateOnFocus: false,
    },
  )

  const mutateNovel = () => {
    mutate(fetcherKey)
  }

  useEffect(() => {
    if (data) {
      setNovel(data)
    }
  }, [data])

  /** 小説を削除 */
  const deleteNovel = () => {
    if (!novel) return

    const func = async () => {
      try {
        fullScreenLoading(true)
        await deleteNovelFn(novel.id)
        addToastInfo(`${novel.title}を削除しました`)
        router.push(routes.creatorMyPage())
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      } finally {
        fullScreenLoading(false)
      }
    }

    confirmDialog(`${novel.title}を削除しますか？`, "はい", "いいえ", func)
  }

  return {
    novel: novel,
    error,
    mutateNovel,
    deleteNovel,
  }
}

export { useNovel }
